<template>
  <aside class="details-panel" :class="{ 'has-content': !!detail }">
    <div v-if="!detail" class="details-empty">
      <svg aria-hidden="true" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" style="opacity:0.4;"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
      <div class="details-empty-title">Hover over a ticket or add-on</div>
      <div class="details-empty-sub">Details appear here — what's included, capacity, refund policy, and any constraints to know about.</div>
    </div>

    <div v-else class="details-content">
      <div class="details-eyebrow">{{ detail.eyebrow }}</div>
      <div class="details-title">{{ detail.title }}</div>
      <div v-if="detail.price !== null && detail.price !== undefined" class="details-price">{{ detail.currencySymbol || '$' }}{{ formatPrice(detail.price) }}<span class="currency">{{ detail.currency || 'USD' }}</span></div>
      <div v-if="detail.description" class="details-section">
        <h4>What's included</h4>
        <!-- Plain text from the organizer's textarea; pre-wrap keeps line breaks and spacing as typed. -->
        <div class="details-desc is-plain">{{ detail.description }}</div>
      </div>
      <div v-else-if="detail.includes && detail.includes.length" class="details-section">
        <h4>What's included</h4>
        <ul class="details-list">
          <li v-for="(inc, i) in detail.includes" :key="i">{{ inc }}</li>
        </ul>
      </div>
      <div v-if="detail.noteHtml || detail.note" class="details-callout">
        <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
        <span v-if="detail.noteHtml" v-html="detail.noteHtml"></span>
        <span v-else>{{ detail.note }}</span>
      </div>
    </div>
  </aside>
</template>

<script>
export default {
  name: 'lp-details-panel',
  props: {
    // null → empty state; otherwise { eyebrow, title, price, description, includes:[], note }
    detail: { type: Object, default: null },
  },
  methods: {
    formatPrice(n) { return Number(n).toLocaleString() },
  },
}
</script>

<style lang="scss">
.details-panel {
  background: var(--surface);
  border: 0.5px solid var(--border);
  border-radius: var(--radius-lg);
  padding: 24px;
  position: sticky;
  top: var(--details-sticky-top, 200px);
  min-height: 280px;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.details-panel.has-content {
  border-color: var(--brand);
  box-shadow: 0 0 0 1px var(--brand-soft);
}
.details-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  height: 100%;
  min-height: 240px;
  color: var(--text-tertiary);
  gap: 8px;
}
.details-empty-title { font-size: var(--fs-body); font-weight: 500; color: var(--text-secondary); margin-top: 4px; }
.details-empty-sub { font-size: var(--fs-small); color: var(--text-tertiary); line-height: 1.5; max-width: 240px; }

.details-content { display: none; }
.details-panel.has-content .details-empty { display: none; }
.details-panel.has-content .details-content { display: block; }

.details-eyebrow {
  font-size: var(--fs-caption);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--brand-deep);
  font-weight: 500;
  margin-bottom: 6px;
}
.details-title {
  font-size: var(--fs-lead-lg);
  font-weight: 500;
  line-height: 1.35;
  margin-bottom: 4px;
  color: var(--text);
}
.details-price {
  font-size: var(--fs-h4);
  font-weight: 500;
  color: var(--brand-deep);
  margin-bottom: 14px;
}
.details-price .currency { font-size: var(--fs-body); color: var(--text-secondary); margin-left: 4px; font-weight: 400; }
.details-section {
  border-top: 0.5px solid var(--border);
  padding-top: 14px;
  margin-top: 14px;
}
.details-section h4 {
  font-size: var(--fs-label);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-tertiary);
  margin: 0 0 8px;
  font-weight: 500;
}
.details-section p {
  font-size: var(--fs-small);
  line-height: 1.6;
  margin: 0 0 8px;
  color: var(--text-secondary);
}
.details-section p:last-child { margin-bottom: 0; }
.details-list {
  margin: 0;
  padding-left: 18px;
  font-size: var(--fs-small);
  line-height: 1.7;
  color: var(--text-secondary);
}
.details-list li { margin-bottom: 4px; }
.details-desc {
  font-size: var(--fs-small);
  line-height: 1.6;
  color: var(--text-secondary);
}
/* Below this width .tickets-area stacks and there is no hover; add-on
   descriptions open inline in lp-ticket-card instead. */
@media (max-width: 1100px) {
  .details-panel { display: none; }
}
.details-desc.is-plain { white-space: pre-wrap; overflow-wrap: anywhere; }
.details-desc ul { margin: 0; padding-left: 18px; }
.details-desc p { margin: 0 0 8px; }
.details-callout {
  background: var(--brand-soft);
  border-radius: var(--radius-sm);
  padding: 10px 12px;
  font-size: var(--fs-label);
  color: var(--brand-deep);
  line-height: 1.5;
  margin-top: 10px;
  display: flex;
  gap: 8px;
  align-items: flex-start;
}
.details-callout svg { flex-shrink: 0; margin-top: 1px; }
</style>
